import { useState, useEffect } from 'react';

// Material-UI
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell as _TableCell,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { styled, CircularProgress, Typography, Box, Button } from '@mui/material';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';

export default function ScanStatus() {
    const [state, setState] = useState({
        data: null,
        loading: true,
        error: false,
    });

    const { data, loading, error } = state;

    useEffect(() => {
        fetchExpresso(
            `/admin/reports/scan_status?tz=${moment.tz.guess()}&start=${moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss')}&end=${moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss')}`,
        )
            .then(async (res) => {
                if (res.status !== 200) {
                    setState((s) => ({ ...s, loading: false, error: true }));
                    return;
                }
                const data = await res.json();
                setState((s) => ({ ...s, data: data, loading: false, error: false }));
            })
            .catch((e) => {
                setState((s) => ({ ...s, loading: false, error: true }));
            });
    }, []);

    if (loading) {
        return (
            <Root>
                <Status>
                    <CircularProgress disableShrink />
                </Status>
            </Root>
        );
    }

    if (error) {
        return (
            <Root>
                <Status>
                    <Typography>Unable to load stats</Typography>
                </Status>
            </Root>
        );
    }

    const totalScans = data.reduce((p, n) => p + n.status_count, 0);

    return (
        <Root>
            <CardHeader
                title="Scans"
                action={<Button style={{ textDecoration: 'underline' }}>Today</Button>}
            />

            <CardContent>
                <TableContainer component={Box}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCellBold></TableCellBold>
                                <TableCellBold></TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        height={'100%'}
                                        display="flex"
                                        alignItems={'center'}
                                        justifyContent={'flex-end'}
                                    >
                                        <ScanIcon />
                                    </Box>
                                </TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        height={'100%'}
                                        display="flex"
                                        alignItems={'center'}
                                        justifyContent={'flex-end'}
                                    >
                                        <GoodIcon />
                                    </Box>
                                </TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                    >
                                        WHEN
                                        <ArrowDropDownIcon fontSize="small" />
                                    </Box>
                                </TableCellBold>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {data.map((s) => (
                                <TableRow key={s.scan_status_id}>
                                    <TableCellBold width={20}>{s.scan_status_id}</TableCellBold>
                                    <TableCell>{s.code_desc}</TableCell>
                                    <TableCell align="right">{s.status_count}</TableCell>
                                    <TableCell align="right">
                                        {((s.status_count / totalScans) * 100).toFixed(1)}%
                                    </TableCell>
                                    <TableCell align="right">
                                        {getRelativeTime(s.latest_status_stamp, 'short')}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {data.length === 0 && (
                                <TableRow>
                                    <TableCell align="center" colSpan={5}>
                                        <Typography variant="subtitle2">No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Root>
    );
}

const Root = styled(Card)({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
});

const Status = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
});

const TableCellBold = styled(_TableCell)(({ theme }) => ({
    fontWeight: 'bold',
}));

const TableCell = styled(_TableCell)(({ theme }) => ({
    whiteSpace: 'nowrap',
}));

const ScanIcon = styled(CenterFocusStrongIcon)(({ theme }) => ({
    height: 18,
    width: 18,
}));

const GoodIcon = styled(CheckIcon)(({ theme }) => ({
    height: 18,
    width: 18,
}));
